import { useEffect } from 'react';
import { Box } from '@nimbus-ds/components';
import { Country } from '@tiendanube/common/src/enums';
import { useNotifications } from 'App/capacitor';
import FeatureFlag from 'App/components/FeatureFlag';
import OnboardingCompletedAlert from 'App/components/OnboardingCompletedAlert';
import ShowByRole from 'App/components/ShowByRole/ShowByRole';
import {
  NUVEM_CHAT,
  ONBOARDING_PROMOTE_V2,
  PROJECT_SHIVA_TAG,
} from 'App/featuresFlags';
import {
  HeaderContent,
  IonPageStratus,
} from 'commons/components/IonPageStratus';
import { LendingAwareness } from 'commons/components/LendingAwareness/LendingAwareness';
import { useRoles } from 'domains/Auth/authSlice/useRoles';
import { useGetCountry, useGetTags } from 'domains/Auth/hooks';
import { useTransactionFeeToPay } from 'domains/Billing/TransactionFees/pages/TransactionFeesPage/hooks';
import PartnerAdminAlert from 'domains/Configurations/Notifications/pages/NotificationsListPage/components/PartnerAdminAlert';
import { useTranslationDashboard } from 'domains/Dashboard/hooks';
import { useTotalOrders } from 'domains/Orders/Orders/hooks';
import { useHasAccessToStats } from 'domains/Statistics';
import {
  ActivatePushNotificationsAlert,
  AlertNotifications,
  AlertPromote,
  DashboardHelpLink,
  DashboardModals,
  ExpiresInChip,
  GiftCard,
  HeaderPage,
  NewAlertNotifications,
  NewEcommerceNaPraticaCard,
  NewOnboardingTasks,
  NuvemEnvioBanners,
  OnboardingPromoteTasks,
  PendingOrders,
  PromoteList,
  RecommendationsList,
  StoreStats,
  UpdateNotificationsAlert,
} from './components';
import useOnboardingTasks from './components/OnboardingTasks/useOnboardingTasks';
import { PromoteAccordion } from './components/PromoteAccordion';
import { SalesChannelsRecommendation } from './components/SalesChannelsRecommendation';

function NewDashboardPage() {
  const t = useTranslationDashboard();
  const { isLoadingHasOrders, hasOrder, totalAllOrders } = useTotalOrders();
  const { validateRoles } = useRoles();
  const hasAccessToStats = useHasAccessToStats();
  const tags = useGetTags();
  const country = useGetCountry();
  const requireTransactionFeesFetch = validateRoles([
    'full',
    'nuvem_pago',
    'nuvem_envio',
  ]);
  useTransactionFeeToPay(requireTransactionFeesFetch);
  const {
    getOnboarding,
    hasCompleted,
    hasCompletedOrSkipped,
    isPos,
    isOnlineStore,
    onboardingTasks,
    isLoading: isLoadingOnboardingTasks,
    isSuccess: isSuccessOnboardingTasks,
  } = useOnboardingTasks();
  useNotifications();
  const hasOrderRoleNonFull = validateRoles('orders', 'full');
  const hasOrderRoleStatsNonFull = validateRoles('statistics', 'full');
  const hasRoleFull = validateRoles('full');
  const hasOnboardingIncomplete = !hasCompletedOrSkipped;
  const showCompletedAlert = hasCompleted && totalAllOrders < 3;
  const hasPromoteV2 =
    tags.some((t) => t.startsWith(ONBOARDING_PROMOTE_V2)) ||
    (onboardingTasks?.promoteGroupedTasks &&
      onboardingTasks?.promoteGroupedTasks?.length > 0);
  const hasNuvemChat = tags.includes(NUVEM_CHAT);
  const showSalesChannels =
    isSuccessOnboardingTasks &&
    (hasPromoteV2 || hasNuvemChat || country === Country.BR);

  useEffect(() => {
    getOnboarding();
  }, [getOnboarding]);

  const showPendingOrders =
    ((hasRoleFull && (hasCompleted || hasOrder)) || hasOrderRoleNonFull) &&
    hasOrder;

  const showEcommerceNaPractica = isOnlineStore && showCompletedAlert;

  return (
    <IonPageStratus
      width="small"
      headerContent={<HeaderContent title={t('title')} />}
      headerTop={<ActivatePushNotificationsAlert />}
    >
      <UpdateNotificationsAlert />
      <PartnerAdminAlert showDetailsButton />
      <Box display="flex" flexDirection="column" gap="6">
        <Box display="flex" flexDirection="column" gap="4">
          <ExpiresInChip />
          {showCompletedAlert && <OnboardingCompletedAlert />}
          {isSuccessOnboardingTasks &&
            (hasOnboardingIncomplete ? (
              <>
                <NewAlertNotifications />
                <FeatureFlag flag={PROJECT_SHIVA_TAG}>
                  <GiftCard />
                </FeatureFlag>
              </>
            ) : (
              <AlertNotifications />
            ))}

          {/* Display Pending Orders block if the user has the "full" role and has completed the onboarding or has sales */}
          {/* Display Pending Orders block always if the user has only the "orders" role */}
          {showPendingOrders && (
            <ShowByRole includeRoles="orders">
              <PendingOrders />
            </ShowByRole>
          )}

          {/* Display the Statistics block if the user has "orders" and "statistics" roles or "full" role, and has orders */}
          {(hasOrderRoleNonFull || hasRoleFull) &&
            hasAccessToStats &&
            !isLoadingHasOrders && (
              <ShowByRole includeRoles="statistics">
                <StoreStats showDailyStats={hasOrder} />
              </ShowByRole>
            )}

          {/* Display the Statistics block if the user has only the "statistics" role */}
          {!hasOrderRoleNonFull &&
            hasOrderRoleStatsNonFull &&
            hasAccessToStats && (
              <ShowByRole includeRoles="statistics">
                <StoreStats />
              </ShowByRole>
            )}

          <NuvemEnvioBanners />

          {hasOnboardingIncomplete && (
            <Box display="flex" flexDirection="column" gap="6">
              <HeaderPage />
              <NewOnboardingTasks />
            </Box>
          )}
        </Box>
        {hasPromoteV2 ? (
          <PromoteAccordion
            tasks={onboardingTasks?.promoteGroupedTasks ?? []}
          />
        ) : (
          <>
            <PromoteList tasks={onboardingTasks?.promoteTasks ?? []} />
            {isLoadingOnboardingTasks && !onboardingTasks?.promoteTasks && (
              <OnboardingPromoteTasks.Skeleton type="promote" itemsCount={3} />
            )}
          </>
        )}
        {showEcommerceNaPractica && (
          <ShowByRole includeRoles="full">
            <NewEcommerceNaPraticaCard />
          </ShowByRole>
        )}
        {showSalesChannels && (
          <SalesChannelsRecommendation
            tasks={onboardingTasks?.recommendationChannelTasks}
            hasIncompleteOnboarding={hasOnboardingIncomplete}
          />
        )}

        <RecommendationsList
          tasks={onboardingTasks?.recommendationTasks ?? []}
        />
        {!hasOnboardingIncomplete && (
          <LendingAwareness app="dashboard" variant="white" />
        )}
        <AlertPromote />
      </Box>
      <DashboardModals />
      <DashboardHelpLink
        isPos={isPos}
        hasOnboardingIncomplete={hasOnboardingIncomplete}
      />
    </IonPageStratus>
  );
}

export default NewDashboardPage;
